import { useRef } from 'react'

import {
  AtsGetSalaryConditionsDocument,
  AtsJobPostingSalaryFormatEnum,
  AtsJobPostingSalaryPeriodEnum,
} from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

interface SalaryFormValues {
  salaryFormat: AtsJobPostingSalaryFormatEnum
  salaryPeriod: AtsJobPostingSalaryPeriodEnum | null
  salaryFromAmountInCents: number | null
  salaryToAmountInCents: number | null
  salaryCurrency: string | null
}

interface UseSalaryConditionsAutoFillParams {
  jobCatalogLevelUuid: string | null
  workplaceId: string
  onSalaryConditionsLoaded: (values: SalaryFormValues) => void
}

const isValidPeriodicity = (value: string): value is AtsJobPostingSalaryPeriodEnum =>
  value === 'annual' || value === 'monthly' || value === 'daily'

const normalizePeriodicity = (value: string): AtsJobPostingSalaryPeriodEnum | null => {
  if (value === 'yearly') return 'annual'
  if (isValidPeriodicity(value)) return value
  return null
}

export const useSalaryConditionsAutoFill = ({
  jobCatalogLevelUuid,
  workplaceId,
  onSalaryConditionsLoaded,
}: UseSalaryConditionsAutoFillParams) => {
  const salaryValues = useRef(onSalaryConditionsLoaded)
  salaryValues.current = onSalaryConditionsLoaded

  const { data: salaryConditionsData } = useQuery(AtsGetSalaryConditionsDocument, {
    variables: {
      levelNodeUuid: jobCatalogLevelUuid ?? '',
      workplaceId: workplaceId,
    },
    skip: !jobCatalogLevelUuid || !workplaceId,
  })

  const salaryConditions = salaryConditionsData?.ats.salaryConditionsConnection.nodes[0]

  useUnsafeEffect(() => {
    if (!salaryConditions) {
      return
    }

    const { min, max, gross, periodicity, currency } = salaryConditions

    const salaryFormat: AtsJobPostingSalaryFormatEnum = gross ? 'fixed_amount' : 'range'
    const salaryFrom = gross ?? min
    const salaryTo = max
    const salaryPeriod = normalizePeriodicity(periodicity)

    salaryValues.current({
      salaryFormat,
      salaryPeriod,
      salaryFromAmountInCents: salaryFrom ?? null,
      salaryToAmountInCents: salaryTo ?? null,
      salaryCurrency: currency ?? null,
    })
  }, [salaryConditions])
}
